import axios from "axios";
export const uuid = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const base = String(process.env.REACT_APP_NOT_SECRET_CODE || "").replace(/\/$/, "");
export const campaignBase = `${/\/api\/v1$/i.test(base) ? base : `${base}/api/v1`}/ai-campaigns`;
export const cancelSource = () => axios.CancelToken.source();
export const isCancelled = axios.isCancel;
// Legacy login omits activity. Resolve it from the existing authenticated
// identity endpoint before exposing this module; never infer it from role.
export async function resolveCampaignUser(user, cancelToken) {
  if (!user || Number(user.role) !== 1 || user.is_active !== undefined) return user;
  const response = await axios.get(campaignBase.replace(/\/v1\/ai-campaigns$/, "/user"), {
    cancelToken, timeout: 30000,
    headers: { Authorization: `Bearer ${user.token}`, "X-Country-Code": "LK", "X-Client-Name": "CRM", "X-Client-Version": "123", "X-Request-ID": uuid() }
  });
  return { ...response.data, token: user.token };
}
// Serialize reservations across tabs where Web Locks are available. Only timing
// metadata is shared; tokens and campaign data never enter this budget.
export const CAMPAIGN_REQUEST_LIMIT = 100;
const budgetKey = `campaign-request-budget:${campaignBase}`;
const withBudgetLock = action => window.navigator.locks
  ? window.navigator.locks.request(budgetKey, action) : action();
let requestTimes = [];
let reservation = Promise.resolve();
let blockedUntil = 0;
function readBudget() {
  try {
    const budget = JSON.parse(localStorage.getItem(budgetKey) || "{}");
    requestTimes = (Array.isArray(budget.times) ? budget.times : []).filter(time => Number.isFinite(time) && Date.now() - time < 60000);
    blockedUntil = Math.max(blockedUntil, Number(budget.blockedUntil) || 0);
  } catch (_) { /* Storage is optional; the in-memory limiter still applies. */ }
}
const saveBudget = () => {
  try { localStorage.setItem(budgetKey, JSON.stringify({ times: requestTimes, blockedUntil })); }
  catch (_) { /* No campaign data is stored here. */ }
};
function reserveRequest(cancelToken) {
  const next = reservation.then(async () => {
    // Recheck after every wait: another tab or a 429 can extend the cooldown.
    for (;;) {
      if (cancelToken) cancelToken.throwIfRequested();
      const waitUntil = await withBudgetLock(() => {
        readBudget();
        requestTimes = requestTimes.filter(time => Date.now() - time < 60000);
        const limit = window.navigator.locks ? CAMPAIGN_REQUEST_LIMIT : 24;
        const until = Math.max(blockedUntil, requestTimes.length >= limit ? requestTimes[0] + 60050 : 0);
        if (until > Date.now()) return until;
        if (cancelToken) cancelToken.throwIfRequested();
        requestTimes.push(Date.now());
        saveBudget();
        return 0;
      });
      if (!waitUntil) return;
      await new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, waitUntil - Date.now());
        if (cancelToken) cancelToken.promise.then(reason => { clearTimeout(timer); reject(reason); });
      });
    }
  });
  reservation = next.catch(() => {});
  return next;
}
export async function campaignRequest(token, method, path = "", data, cancelToken) {
  await reserveRequest(cancelToken);
  let response;
  try { response = await axios({
    method,
    url: campaignBase + path,
    data,
    cancelToken,
    timeout: 60000,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-Country-Code": "LK",
      "X-Client-Name": "CRM",
      "X-Client-Version": "123",
      "X-Request-ID": uuid()
    }
  }); } catch (error) {
    if (error.response && error.response.status === 429) {
      const retry = (error.response.headers || {})["retry-after"];
      const seconds = Number(retry);
      await withBudgetLock(() => {
        readBudget();
        blockedUntil = Math.max(blockedUntil, Number.isFinite(seconds) && seconds > 0 ? Date.now() + seconds * 1000 : Date.parse(retry) || Date.now() + 60000);
        saveBudget();
      });
    }
    throw error;
  }
  if (!response.data || response.data.success !== true) throw new Error((response.data || {}).message || "Unexpected campaign response.");
  return response.data;
}

// Endpoint map for the Campaign Studio screens. Every call goes through the
// shared request budget above; containers own cancellation.
export const campaignApi = {
  getWorkspace: (token, page, cancelToken) => campaignRequest(token, "get", `?page=${page}`, undefined, cancelToken),
  getAnalytics: (token, cancelToken) => campaignRequest(token, "get", "/analytics", undefined, cancelToken),
  getCampaign: (token, id, cancelToken) => campaignRequest(token, "get", `/${id}`, undefined, cancelToken),
  getOutcomes: (token, id, windowDays, cancelToken) => campaignRequest(token, "get", `/${id}/outcomes?window=${windowDays}`, undefined, cancelToken),
  getLogs: (token, id, page, cancelToken) => campaignRequest(token, "get", `/${id}/logs?page=${page}`, undefined, cancelToken),
  getAudience: (token, id, page, day, cancelToken) => campaignRequest(token, "get", `/${id}/audience?page=${page}${day ? `&day=${day}` : ""}`, undefined, cancelToken),
  getPreview: (token, id, leadId, cancelToken) => campaignRequest(token, "get", `/${id}/preview${leadId ? `?lead_id=${encodeURIComponent(leadId)}` : ""}`, undefined, cancelToken),
  planScenarios: (token, body, cancelToken) => campaignRequest(token, "post", "/plan", body, cancelToken),
  selectScenario: (token, cycleId, body, cancelToken) => campaignRequest(token, "post", `/cycles/${cycleId}/select`, body, cancelToken),
  retryAudience: (token, id, cancelToken) => campaignRequest(token, "post", `/${id}/audience/retry`, {}, cancelToken),
  generateContent: (token, id, body, cancelToken) => campaignRequest(token, "post", `/${id}/generate`, body, cancelToken),
  retryGeneration: (token, id, cancelToken) => campaignRequest(token, "post", `/${id}/generate/retry`, {}, cancelToken),
  saveContent: (token, id, body, cancelToken) => campaignRequest(token, "post", `/${id}`, body, cancelToken),
  sendTest: (token, id, body, cancelToken) => campaignRequest(token, "post", `/${id}/test`, body, cancelToken),
  approve: (token, id, body, cancelToken) => campaignRequest(token, "post", `/${id}/approve`, body, cancelToken),
  activate: (token, id, body, cancelToken) => campaignRequest(token, "post", `/${id}/activate`, body, cancelToken),
  pause: (token, id, cancelToken) => campaignRequest(token, "post", `/${id}/pause`, {}, cancelToken),
  resume: (token, id, cancelToken) => campaignRequest(token, "post", `/${id}/resume`, {}, cancelToken),
  stop: (token, id, cancelToken) => campaignRequest(token, "post", `/${id}/stop`, {}, cancelToken),
  restart: (token, id, cancelToken) => campaignRequest(token, "post", `/${id}/restart`, {}, cancelToken)
};
